// ============================================================================
// Modal primitive — shared open/close/overlay/focus-trap for all modal overlays
// ============================================================================
// Replaces the 5+ ad-hoc modal patterns (fv-overlay, picker-overlay,
// terminal-overlay, stats-overlay, sessions-overlay) that each reimplement
// open/close/overlay-click/ESC handling with subtly different semantics.

/**
 * Wire up a modal overlay with standard behavior:
 * - Click on the overlay backdrop (not the modal content) → close
 * - ESC key → close (if this modal is the topmost open one)
 * - Close button → close
 *
 * @param {object} opts
 * @param {HTMLElement} opts.overlay - the overlay element (has .hidden class)
 * @param {HTMLElement} [opts.closeBtn] - the close button
 * @param {function} [opts.onOpen] - called after opening
 * @param {function} [opts.onClose] - called after closing
 * @param {boolean} [opts.useHiddenClass] - true if overlay uses .hidden class (default), false if uses .classList "hidden" toggle
 */
export function wireModal({ overlay, closeBtn, onOpen, onClose }) {
  if (!overlay) return;

  // Overlay backdrop click → close
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) {
      closeModal(overlay, onClose);
    }
  });

  // Close button
  if (closeBtn) {
    closeBtn.addEventListener("click", () => {
      closeModal(overlay, onClose);
    });
  }
}

/**
 * Open a modal overlay by removing the "hidden" class.
 * Calls onOpen callback if provided.
 */
export function openModal(overlay, onOpen) {
  if (!overlay) return;
  overlay.classList.remove("hidden");
  if (onOpen) onOpen();
}

/**
 * Close a modal overlay by adding the "hidden" class.
 * Calls onClose callback if provided.
 */
export function closeModal(overlay, onClose) {
  if (!overlay) return;
  overlay.classList.add("hidden");
  if (onClose) onClose();
}

/**
 * Check if a modal overlay is currently open (not hidden).
 */
export function isModalOpen(overlay) {
  return overlay && !overlay.classList.contains("hidden");
}

/**
 * Register a modal for ESC-key handling. The modals are checked in
 * registration order; the first open modal receives the ESC close.
 * Call this once per modal at init time.
 *
 * @param {HTMLElement} overlay - the overlay element
 * @param {function} [onClose] - optional close callback
 * @returns {function} - call to close this modal (for use in ESC chain)
 */
const escModals = [];

export function registerEscModal(overlay, onClose) {
  escModals.push({ overlay, onClose });
}

/**
 * Global ESC handler — closes the topmost open modal.
 * Call this once at init time.
 */
export function initEscHandler() {
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    // Walk in reverse registration order (topmost modal first)
    for (let i = escModals.length - 1; i >= 0; i--) {
      const { overlay, onClose } = escModals[i];
      if (isModalOpen(overlay)) {
        closeModal(overlay, onClose);
        return;
      }
    }
  });
}
